import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Box, Button, MenuItem, Theme } from '@material-ui/core';
import { ArrowDropDown, ArrowDropUp, CloseOutlined, Menu, Rowing as RowingIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { AuthButton } from './auth/auth-button.component';
import { useAuth, useAuthUsers } from './auth/auth.context';
import './App.css';
import { isAdmin, isAtLeastPartner, isAtLeastSeller, isDriver } from './auth/roles';
import AddIcon from '@material-ui/icons/Add';
import { Theme as DefaultTheme } from '@material-ui/core/styles/createMuiTheme';

/**
 * Styles pour la sidebar de navigation
 * 
 * @param theme Thème Material-UI
 * @param isOpen État d'ouverture de la sidebar
 * @returns Styles CSS-in-JS
 */
const useStyles = makeStyles<DefaultTheme, { isOpen: boolean }>(
  (theme: Theme) => ({
    header: {
      backgroundColor: theme.palette.grey['300'],
      position: 'fixed',
      height: '100%',
      left: 0,
      top: 0,
      overflowX: 'hidden',
      width: '200px',
      zIndex: 10,
      // display: ({ isOpen }) => (isOpen ? 'flex' : 'none'),
      [theme.breakpoints.down('sm')]: {
        display: ({ isOpen }) => (isOpen ? 'inherit' : 'none')
      },
      [theme.breakpoints.down('xs')]: {
        width: '100vw',
        "& label" : {
          fontSize: '1.2rem'
        },
        "& a, & span, & li" : {
          fontSize: '1rem'
        },
        "& button svg" : {
          fontSize: '2.4rem'
        }
      },
    },
    headerInner: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      padding: 10,
      gap: 5,
      height: 'calc(100% - 50px)',
      borderRight: `1px solid ${theme.palette.grey['500']}`,
      [theme.breakpoints.up('md')]: {
        height: '100%',
      },
    },
    open: {
      backgroundColor: theme.palette.grey['300'],
      border: `1px solid ${theme.palette.grey['500']}`,
      borderRadius: 20,
      position: 'fixed',
      left: 20,
      top: 20,
      display: 'flex',
      padding: '5px',
      zIndex: 50,

      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },
    close: {
      position: 'fixed',
      bottom: 0,
      height: '3rem',
      width: '200px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.palette.grey['300'],
      borderTop: `1px solid ${theme.palette.grey['500']}`,
      zIndex: 11,
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },
    logo: {
      pointerEvents: 'none',
      margin: '20px auto 0',
      maxWidth: '100%',
      '& img': {
        maxWidth: '100%',
        height: 'auto',
      }
    },
    auth: {
      marginTop: 20,
      marginBottom: 20,
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    authMobile: {
      margin: '1.1rem',
      marginBottom: '1.1rem',
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },
    headerGroupName: {
      padding: '5px',
      border: '1px solid #a5a5a5',
      borderRadius: '4px',
      lineHeight:'0.7rem',
      marginTop: '10px',
      marginBottom: '5px',
      textAlign: 'center',
      width: '100%',
      fontSize: '12px'
    },
    subMenuActive: {
      background: '#d0d0d0',
      borderRadius: '5px',
      fontSize: '0.875rem',
    },
    subMenuInactive: {
      fontSize: '0.875rem',
      borderRadius: '5px',
      background: '#fff'
    },
    fixedMenuText: {
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    }
  }),
);

export const Sidebar: React.FC = () => {
  const auth = useAuth();
  const { domain, groups, loggedUser } = useAuthUsers();
  const isAdminUser = isAdmin(auth.userGroup);
  const isDriverUser = isDriver(auth.userGroup);
  const isSellerUser = isAtLeastSeller(auth.userGroup);
  const isPartnerUser = isAtLeastPartner(auth.userGroup);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const classes = useStyles({ isOpen });
  const authUserGroup = groups.find(g => g._id === auth.userGroup?.userGroupId);
  const [deploySellerMenu, setDeploySellerMenu] = useState<boolean>(false);
  const [deployAdminMenu, setDeployAdminMenu] = useState<boolean>(false);
  const location = useLocation();

  useEffect(() => {
    switch (location.pathname) {
      case '/reservations/in-activity':
      case '/reservations/archive':
      case '/reservations/cancelled':
      case '/reservations/wait-to-paid':
      case '/reservations/refunds':
      case '/reservations/gift-voucher':
      case '/reservations/trash':
      case '/sales':
        setDeploySellerMenu(true);
        break;
      case '/domain':
      case '/user-groups':
      case '/spots':
      case '/activities':
      case '/turnover':
        setDeployAdminMenu(true);
        break;
      default:
        break;
    }
  },[location.pathname]);

  return (
    <>
      <div className={classes.open} onClick={() => setIsOpen(!isOpen)}>
        <Menu />
      </div>
      <header className={`App-header ${classes.header}`}>
        <div className={classes.close} onClick={() => setIsOpen(false)}>
          <CloseOutlined />
        </div>
        <div className={classes.headerInner}>
          {domain?.logoUrl ? (
            <div className={classes.logo}>
              <img src={domain.logoUrl} alt={'logo'} />
            </div>
          ) : (
            <div className={classes.logo}>
              <RowingIcon className="App-icon" />
            </div>
          )}
          <Box display="flex" flexDirection="column">
            {auth?.userGroup && (
            <Box display="block" className={classes.headerGroupName} flexDirection="column">
              <label>point de vente<label/> <br/> <b>{authUserGroup?.displayName || ''}</b></label>
            </Box>)}
            {auth?.userGroup && (
              <div className={classes.auth}>
                <AuthButton />
              </div>
            )}
            {auth?.userGroup && (
              <div className={classes.authMobile}>
                <AuthButton />
              </div>
            )}
            {isPartnerUser && (
              <Box mb={2} mr={1}>
                <Link to="/reservations/create" onClick={() => setIsOpen(false)}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    size="medium"
                    startIcon={<AddIcon />}
                  >
                    Réserver
                  </Button>
                </Link>
              </Box>
            )}
            {(isAdminUser || auth?.userGroup?.haveSupervisedActivitiesAccess) && (
              <Box mb={4} mr={1}>
                <Link to="/timeslots" onClick={() => setIsOpen(false)}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    size="medium"
                    startIcon={<AddIcon />}
                  >
                    Encadrée
                  </Button>
                </Link>
              </Box>
            )}
            {(isSellerUser || isPartnerUser) && <NavLink to="/prices" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Tarifs</NavLink>}
            {isPartnerUser && (
              <NavLink to="/reservations/futur" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Réservations</NavLink>
            )}
            {isSellerUser && (
              <NavLink to="/reservations/active" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Locations</NavLink>
            )}
            
            {(isDriverUser) && <NavLink to="/driver-travellist" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Ma Liste</NavLink>}
            {(isSellerUser || isDriverUser) && <NavLink to="/departures" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Départs</NavLink>}
            {(isSellerUser) && <NavLink to="/arrivals" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Retours</NavLink>}
            {((isDriverUser && loggedUser?.canManageArrivals === true) || isAdminUser) && 
              <NavLink to="/driver-arrivals" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Retours {isAdminUser ? '(vue chauffeur)' : ''}</NavLink>}
            {/*(isDriverUser) && <NavLink to="/driver-vehicle" className={classes.fixedMenuText}>Mon Véhicule</NavLink>*/}
            {/*(isAdminUser || auth?.userGroup?.haveSupervisedActivitiesAccess) && 
            (
            <NavLink to="/timeslots">Activités Encadrées</NavLink>
            )*/}
            {((isPartnerUser && auth?.userGroup?.canManageTransports) || isAdminUser) && (
              <NavLink to="/vehicles" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Gest. des Transports</NavLink>
            )}
          { isPartnerUser && 
          <Box display="inline" alignItems={'center'} flexDirection="column" mt={1} mb={2}>
            <MenuItem
              className={deploySellerMenu ? classes.subMenuActive : classes.subMenuInactive}
              onClick={() => setDeploySellerMenu(!deploySellerMenu)}
            >
              Plus 
              { !deploySellerMenu && <ArrowDropDown /> }
              { deploySellerMenu && <ArrowDropUp /> }
            </MenuItem>
          </Box>
          }
          { deploySellerMenu && 
            <Box display="flex" flexDirection="column" ml={1}>
              {isPartnerUser && (
                <NavLink to="/sales" onClick={() => setIsOpen(false)}>Fiche de caisse</NavLink>
              )}
              {isSellerUser && (
                <NavLink to="/reservations/in-activity" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Activités en cours</NavLink>
              )}
              {isSellerUser && (
                <NavLink to="/reservations/archive" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Terminées</NavLink>
              )}
              {isPartnerUser && (
                <NavLink to="/reservations/cancelled" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Annulées</NavLink>
              )}
              {isSellerUser && (
                <NavLink to="/reservations/wait-to-paid" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Paiements en attente</NavLink>
              )}
              {isSellerUser && (
                <NavLink to="/reservations/refunds" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Remb. partiels</NavLink>
              )}
              {isSellerUser && (
                <NavLink to="/reservations/gift-voucher" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Bons Cadeaux/Avoirs</NavLink>
              )}
              {isSellerUser && (
                <NavLink to="/reservations/trash" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Corbeille</NavLink>
              )}
            </Box>
          }
          { isAdminUser && 
          <Box display="inline" alignItems={'center'} flexDirection="column" mb={2}>
            <MenuItem
              className={deployAdminMenu ? classes.subMenuActive : classes.subMenuInactive}
              onClick={() => setDeployAdminMenu(!deployAdminMenu)}
            >
              Admin 
              { !deployAdminMenu && <ArrowDropDown /> }
              { deployAdminMenu && <ArrowDropUp /> }
            </MenuItem>
          </Box>
          }
          { deployAdminMenu &&
          <Box display="flex" flexDirection="column" style={{ marginBottom: 'auto'}} ml={1}>
            {isAdminUser && <NavLink to="/domain" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Domaine</NavLink>}
            {isAdminUser && <NavLink to="/user-groups" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Utilisateurs</NavLink>}
            {isAdminUser && <NavLink to="/spots" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Spots</NavLink>}
            {isAdminUser && <NavLink to="/activities" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>Activités</NavLink>}
            {isAdminUser && <NavLink to="/turnover" className={classes.fixedMenuText} onClick={() => setIsOpen(false)}>CA / Stats</NavLink>}
          </Box>
          }
          </Box>
        </div>
      </header>
    </>
  );
};
